import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import AppConfig from "../../../../../config/AppConfig";
import {BookizonAppManager} from "../../../../../index";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {AppIconThumbnail} from "../../../../../components/BaseComponents/AppIconThumbnail";
import {InputDatepicker} from "../../../../../components/BaseComponents/InputDatepicker";
import FuxOffsetPaginator from "../../../../../components/BaseComponents/FuxOffsetPaginator/FuxOffsetPaginator";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {swal, swalConfirm, swalError, swalLoading, swalSuccess} from "../../../../../helpers/SweetAlertWrapper";
import {
    EXPENSE_STATUSES,
    EXPENSE_STATUS_APPROVED,
    EXPENSE_STATUS_BADGE_CLASSES,
    EXPENSE_STATUS_WAITING_REVIEW,
    EXPENSE_TYPES,
    EXPENSE_TYPE_GENERIC
} from "./ExpensesConstants";

const MAX_ATTACHMENTS = 2;

export default class ExpenseDetailPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            expense: null,
            attachments: [],
            type: "generic",
            date: "",
            dateDisplay: "",
            amount: "",
            notes: "",
            pendingAttachments: [],
            logsVersion: 0
        };
    }

    componentDidMount() {
        this.fetchDetails();
    }

    componentDidUpdate(prevProps) {
        const prevExpenseId = prevProps.expense?.expense_id;
        const nextExpenseId = this.props.expense?.expense_id;
        if (prevExpenseId !== nextExpenseId) {
            this.fetchDetails();
        }
    }

    getStatusInfo = status => ({
        label: EXPENSE_STATUSES[status] || EXPENSE_STATUSES[EXPENSE_STATUS_WAITING_REVIEW],
        className: EXPENSE_STATUS_BADGE_CLASSES[status] || EXPENSE_STATUS_BADGE_CLASSES[EXPENSE_STATUS_WAITING_REVIEW]
    });

    isEditable = _ => {
        const expense = this.state.expense || {};
        return expense.status !== EXPENSE_STATUS_APPROVED;
    }

    fetchDetails = _ => {
        const expenseId = this.props.expense?.expense_id;
        if (!expenseId) {
            this.setState({loading: false, error: "Spesa non valida"});
            return;
        }

        this.setState({loading: true, error: null});
        ModuloHRAPI.Expenses.details(expenseId)
            .then(data => {
                const expense = data?.expense || null;
                const attachments = data?.attachments || expense?.attachments || [];
                const date = expense?.date || "";
                const dateDisplay = date ? moment(date).format("DD-MM-YYYY") : "";
                this.setState({
                    loading: false,
                    error: null,
                    expense: expense,
                    attachments: attachments,
                    type: expense?.type || EXPENSE_TYPE_GENERIC,
                    date: date,
                    dateDisplay: dateDisplay,
                    amount: expense?.amount || "",
                    notes: expense?.notes || "",
                    pendingAttachments: [],
                    logsVersion: this.state.logsVersion + 1
                });
            })
            .catch(error => {
                this.setState({
                    loading: false,
                    error: error || "Non e' stato possibile caricare il dettaglio spesa"
                });
            });
    }

    handleTypeChange = e => this.setState({type: e.target.value});
    handleDateChange = e => {
        const selectedDate = e.value;
        this.setState({
            date: moment(selectedDate).format("YYYY-MM-DD"),
            dateDisplay: moment(selectedDate).format("DD-MM-YYYY")
        });
    }
    handleAmountChange = e => this.setState({amount: e.target.value});
    handleNotesChange = e => this.setState({notes: e.target.value});

    validateForm = () => {
        if (!this.state.type) {
            swalError("Specifica un tipo valido");
            return false;
        }
        if (!this.state.date) {
            swalError("Specifica una data valida");
            return false;
        }
        const amount = parseFloat(this.state.amount);
        if (Number.isNaN(amount) || amount <= 0) {
            swalError("Specifica un importo valido");
            return false;
        }
        return true;
    }

    handleSaveClick = _ => {
        if (!this.isEditable()) {
            swalError("Le spese approvate non possono essere modificate");
            return;
        }
        if (!this.validateForm()) return;
        const expense = this.state.expense || {};
        const payload = {
            expense_id: expense.expense_id,
            id_dipendente: expense.id_dipendente,
            trip_id: expense.trip_id,
            type: this.state.type,
            date: this.state.date,
            amount: this.state.amount,
            notes: this.state.notes
        };

        swalLoading();
        ModuloHRAPI.Expenses.save(payload)
            .then(_ => {
                swal.safeClose();
                swalSuccess("Spesa aggiornata");
                this.fetchDetails();
                if (this.props.onUpdated) {
                    this.props.onUpdated();
                }
            })
            .catch(error => {
                swal.safeClose();
                swalError(error || "Non e' stato possibile aggiornare la spesa");
            });
    }

    handleDeleteClick = _ => {
        if (!this.isEditable()) {
            swalError("Le spese approvate non possono essere modificate");
            return;
        }
        const expense = this.state.expense || {};
        if (!expense.expense_id) return;
        swalConfirm("Vuoi eliminare questa spesa?")
            .then(_ => {
                swalLoading();
                return ModuloHRAPI.Expenses.delete(expense.expense_id)
                    .then(_ => {
                        swal.safeClose();
                        swalSuccess("Spesa eliminata");
                        if (this.props.onDeleted) {
                            this.props.onDeleted();
                        }
                        this.props.onDismiss();
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error || "Non e' stato possibile eliminare la spesa");
                    });
            })
            .catch(_ => null);
    }

    handlePendingAttachmentsChange = e => {
        const files = Array.from(e.target.files || []);
        const availableSlots = MAX_ATTACHMENTS - this.state.attachments.length;
        if (files.length > availableSlots) {
            swalError(`Puoi caricare al massimo ${availableSlots} allegati`);
            return;
        }
        this.setState({pendingAttachments: files});
    }

    handleRemovePendingAttachment = index => _ => {
        const pendingAttachments = this.state.pendingAttachments.filter((_, i) => i !== index);
        this.setState({pendingAttachments: pendingAttachments});
    }

    handleUploadAttachments = _ => {
        if (!this.isEditable()) {
            swalError("Le spese approvate non possono essere modificate");
            return;
        }
        if (!this.state.pendingAttachments.length) {
            swalError("Seleziona almeno un allegato");
            return;
        }
        const expense = this.state.expense || {};
        const availableSlots = MAX_ATTACHMENTS - this.state.attachments.length;
        if (this.state.pendingAttachments.length > availableSlots) {
            swalError(`Puoi caricare al massimo ${availableSlots} allegati`);
            return;
        }

        swalLoading();
        ModuloHRAPI.Expenses.uploadAttachments(expense.expense_id, this.state.pendingAttachments)
            .then(_ => {
                swal.safeClose();
                swalSuccess("Allegati caricati");
                this.fetchDetails();
                if (this.props.onUpdated) {
                    this.props.onUpdated();
                }
            })
            .catch(error => {
                swal.safeClose();
                swalError(error || "Non e' stato possibile caricare gli allegati");
            });
    }

    handleDeleteAttachment = attachment => _ => {
        if (!this.isEditable()) {
            swalError("Le spese approvate non possono essere modificate");
            return;
        }
        const expense = this.state.expense || {};
        if (!attachment?.id_file || !expense.expense_id) return;
        swalConfirm("Vuoi eliminare questo allegato?")
            .then(_ => {
                swalLoading();
                return ModuloHRAPI.Expenses.deleteAttachment(expense.expense_id, attachment.id_file)
                    .then(_ => {
                        swal.safeClose();
                        swalSuccess("Allegato eliminato");
                        this.fetchDetails();
                        if (this.props.onUpdated) {
                            this.props.onUpdated();
                        }
                    })
                    .catch(error => {
                        swal.safeClose();
                        swalError(error || "Non e' stato possibile eliminare l'allegato");
                    });
            })
            .catch(_ => null);
    }

    handleOpenAttachment = attachment => _ => {
        if (attachment?.url) {
            const completeUrl = attachment.url.startsWith("http") ? attachment.url : `${AppConfig.webServerUrl}${attachment.url}`;
            BookizonAppManager.openInAppBrowser(completeUrl);
        }
    }

    handleLogsPageRequest = page => {
        const expenseId = this.state.expense?.expense_id;
        if (!expenseId) {
            return Promise.resolve({data: [], total: 0, max_items: 1, pages: 1, prev: null, next: null});
        }
        return ModuloHRAPI.Expenses.logsPagination(expenseId, page);
    }

    renderLogItem = (item, index) => {
        const adminName = item.admin_nome || item.admin_cognome ? `${item.admin_nome || ""} ${item.admin_cognome || ""}`.trim() : "Sistema";
        const statusInfo = this.getStatusInfo(item.status);
        return (
            <div key={item.log_id || index} className={"list-group-item"}>
                <div className={"d-flex justify-content-between align-items-start"}>
                    <span className={statusInfo.className}>{statusInfo.label}</span>
                    <span className={"small text-muted"}>{moment(item.created_at).format("DD/MM/YYYY HH:mm")}</span>
                </div>
                <div className={"small mt-1"}>{item.description || "-"}</div>
                <div className={"small text-muted mt-1"}>{adminName}</div>
            </div>
        );
    }

    renderLogsEmptyPage = _ => {
        return <div className={"text-muted small"}>Nessuno storico disponibile.</div>;
    }

    renderExistingAttachment = (attachment, index) => {
        return (
            <li key={attachment.id_file || index} className={"list-group-item d-flex justify-content-between align-items-center"}>
                <button
                    type={"button"}
                    className={"btn btn-link p-0 text-left"}
                    onClick={this.handleOpenAttachment(attachment)}
                >
                    {attachment.filename || "Allegato"}
                </button>
                {
                    this.isEditable() &&
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-outline-danger"}
                        onClick={this.handleDeleteAttachment(attachment)}
                    >
                        Elimina
                    </button>
                }
            </li>
        );
    }

    renderPendingAttachment = (file, index) => {
        return (
            <li key={index} className={"list-group-item d-flex justify-content-between align-items-center"}>
                <span className={"small w-75 text-truncate"}>{file.name}</span>
                <button
                    type={"button"}
                    className={"btn btn-sm btn-outline-secondary"}
                    onClick={this.handleRemovePendingAttachment(index)}
                >
                    Rimuovi
                </button>
            </li>
        );
    }

    render() {
        if (this.state.loading) {
            return (
                <Page
                    title={"Dettaglio spesa"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={<div className={"container py-4 text-center text-muted"}>Caricamento dettaglio...</div>}
                />
            );
        }

        if (this.state.error || !this.state.expense) {
            return (
                <Page
                    title={"Dettaglio spesa"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={<div className={"container py-4 text-center text-danger"}>{this.state.error || "Spesa non trovata"}</div>}
                />
            );
        }

        const expense = this.state.expense || {};
        const staff = this.props.staff || {};
        const negozio = staff.negozio || null;
        const negozioIconUrl = negozio?.id_negozio
            ? `${AppConfig.webServerUrl}/api/negozio/${negozio.id_negozio}/logoIcon/50`
            : null;
        const statusInfo = this.getStatusInfo(expense.status);
        const updatedAt = expense.updated_at ? moment(expense.updated_at).format("DD/MM/YYYY HH:mm") : "-";
        const canUploadMore = this.state.attachments.length < MAX_ATTACHMENTS && this.isEditable();

        return (
            <Fragment>
                <Page
                    title={"Dettaglio spesa"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={
                        <div className={"container py-3"}>
                            <div className={"d-flex justify-content-between align-items-start"}>
                                <div className={"d-flex align-items-center"}>
                                    <AppIconThumbnail size={"36px"} src={negozioIconUrl} className={"mr-2"}/>
                                    <div>
                                        <div className={"font-weight-bold"}>{negozio?.nome || "Attivita'"}</div>
                                        <div className={"text-muted small"}>Ultimo aggiornamento: {updatedAt}</div>
                                    </div>
                                </div>
                                <span className={statusInfo.className}>{statusInfo.label}</span>
                            </div>

                            <div className={"form-group mt-3"}>
                                <label>Tipo</label>
                                <select
                                    className={"form-control"}
                                    value={this.state.type}
                                    onChange={this.handleTypeChange}
                                    disabled={!this.isEditable()}
                                >
                                    {
                                        Object.keys(EXPENSE_TYPES).map(type => (
                                            <option key={type} value={type}>{EXPENSE_TYPES[type]}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className={"form-group"}>
                                <label>Data</label>
                                <InputDatepicker
                                    className={"form-control bg-main-z3"}
                                    value={this.state.dateDisplay}
                                    onChange={this.handleDateChange}
                                    disabled={!this.isEditable()}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Importo</label>
                                <input
                                    type={"number"}
                                    className={"form-control"}
                                    value={this.state.amount}
                                    step={"0.01"}
                                    min={"0"}
                                    onChange={this.handleAmountChange}
                                    disabled={!this.isEditable()}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Note</label>
                                <textarea
                                    className={"form-control"}
                                    rows={3}
                                    value={this.state.notes}
                                    onChange={this.handleNotesChange}
                                    disabled={!this.isEditable()}
                                />
                            </div>

                            {
                                this.isEditable() &&
                                <div className={"mb-4 d-flex justify-content-between"}>
                                    <button
                                        type={"button"}
                                        className={"btn btn-outline-danger"}
                                        onClick={this.handleDeleteClick}
                                    >
                                        Elimina spesa
                                    </button>
                                    <button
                                        type={"button"}
                                        className={"btn btn-primary"}
                                        onClick={this.handleSaveClick}
                                    >
                                        Salva modifiche
                                    </button>
                                </div>
                            }

                            <div className={"form-group"}>
                                <label>Allegati ({this.state.attachments.length}/{MAX_ATTACHMENTS})</label>
                                {
                                    this.state.attachments.length > 0 ?
                                        <ul className={"list-group"}>
                                            {this.state.attachments.map(this.renderExistingAttachment)}
                                        </ul>
                                        :
                                        <div className={"text-muted small"}>Nessun allegato</div>
                                }
                            </div>

                            {
                                canUploadMore &&
                                <div className={"form-group"}>
                                    <label>Carica allegati</label>
                                    <input
                                        type={"file"}
                                        multiple={true}
                                        className={"form-control-file"}
                                        onChange={this.handlePendingAttachmentsChange}
                                    />
                                    {
                                        this.state.pendingAttachments.length > 0 &&
                                        <ul className={"list-group mt-2"}>
                                            {this.state.pendingAttachments.map(this.renderPendingAttachment)}
                                        </ul>
                                    }
                                    <button
                                        type={"button"}
                                        className={"btn btn-outline-primary mt-2"}
                                        onClick={this.handleUploadAttachments}
                                    >
                                        Carica allegati
                                    </button>
                                </div>
                            }

                            <div className={"mt-4"}>
                                <div className={"small text-muted mb-2"}>Storico stato</div>
                                <FuxOffsetPaginator
                                    key={`expense-logs-${expense.expense_id}-${this.state.logsVersion}`}
                                    onPageRequest={this.handleLogsPageRequest}
                                    onItemRender={this.renderLogItem}
                                    onEmptyPage={this.renderLogsEmptyPage}
                                    classItemsContainer={"list-group"}
                                    visiblePagesCount={3}
                                />
                            </div>

                            {
                                !this.isEditable() &&
                                <div className={"mt-4 text-muted small"}>
                                    La spesa approvata non e' modificabile.
                                </div>
                            }
                        </div>
                    }
                />
            </Fragment>
        );
    }
}

ExpenseDetailPage.propTypes = {
    active: PropTypes.bool,
    onDismiss: PropTypes.func,
    onUpdated: PropTypes.func,
    onDeleted: PropTypes.func,
    expense: PropTypes.object,
    staff: PropTypes.object
};
